import { range } from "lodash";
import React from "react";
import { useTranslation } from "react-i18next";
import { Row } from "@/shared/ui/Blocks";
import { DropdownMenu } from "../DropdownMenu";
import { SelectButton } from "@/shared/ui/SelectButton";
import { cn } from "@/shared/utils/shadcn";

export interface PaginationProps {
  limit?: number;
  pages?: number;
  currentPage?: number;
  itemsCount?: number;
  onPageChange?: (page: number) => void;
  onLimitChange?: (limit: number) => void;
}

function Pagination(props: PaginationProps) {
  const {
    pages = 1,
    currentPage = 1,
    limit = 25,
    itemsCount,
    onPageChange,
    onLimitChange,
  } = props;
  const { t } = useTranslation();

  if (pages <= 0 || (itemsCount && itemsCount <= limit)) return null;

  const pageNumbers = range(1, pages + 1);

  function LimitSelect() {
    return (
      <Row className="items-center space-x-4">
        <p>{t("common.show")}:</p>
        <DropdownMenu
          trigger={<SelectButton size="xs" value={limit.toString()} />}
          separatorIndexes={range(1, 5)}
          side="top"
          items={[15, 25, 50, 100].map((limit) => ({
            value: limit,
            label: `${limit}`,
            selected: limit === props.limit,
            onClick: () => onLimitChange && onLimitChange(limit),
          }))}
        />
      </Row>
    );
  }

  function PaginationButton(props: {
    label: string;
    onClick: () => void;
    selected?: boolean;
  }) {
    return (
      <button
        className={cn(
          "h-[30px] px-2 min-w-[30px] text-sm text-grey-700 border-r rounded-none border-grey-300 last:border-r-0 hover:bg-grey-100 hover:border-grey-300",
          props.selected && "bg-grey-200"
        )}
        onClick={props.onClick}
      >
        {props.label}
      </button>
    );
  }

  function PageSelect() {
    return (
      <Row className="items-center rounded-md border-solid border border-grey-300 overflow-hidden flex-wrap">
        {currentPage > 1 && (
          <PaginationButton
            label={t("common.previous")}
            onClick={() => onPageChange && onPageChange(currentPage - 1)}
          />
        )}
        {pageNumbers.map((page) => (
          <PaginationButton
            key={page}
            label={page.toString()}
            selected={page === currentPage}
            onClick={() => onPageChange && onPageChange(page)}
          />
        ))}
        {currentPage < pages && (
          <PaginationButton
            label={t("common.next")}
            onClick={() => onPageChange && onPageChange(currentPage + 1)}
          />
        )}
      </Row>
    );
  }

  return (
    <Row className="justify-end items-center my-4 space-x-8">
      <LimitSelect />
      <PageSelect />
    </Row>
  );
}

export { Pagination };
